/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../../core/components';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LanguageService, LogService, UtilsService, ValidationService } from '../../../../../../../core/services';
import { NamespaceService } from '../../../../../shared/services/utils';
import { Observable } from 'rxjs';
import { ShuttleApiService } from '../../../../../shared/http';
import { ShuttleModel } from '../../../../../shared/models';
import { ipValidator } from '../../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-edit-subnet',
    templateUrl: './add-edit-subnet.component.html',
    styleUrls: ['./add-edit-subnet.component.scss']
})
export class AddEditSubnetComponent extends BaseComponent implements OnInit {
    /**
     * Stored shuttle id.
     */
    public storedShuttle: ShuttleModel;

    /**
     * Stored subnet id.
     */
    public storedSubnetId: string = '';

    /**
     * New user form group.
     */
    public formGroup: FormGroup;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        gateways: [],
    };

    /**
     * Default selected key for gateway dropdown.
     */
    public selectedKey: any;

    /**
     * Store new gateway state.
     */
    public newGateway: boolean = false;

    /**
     * Namespace selected key.
     * @private
     */
    private selectedNamespaceKey: number;

    /**
     * Stored the selected gateway from the parent page.
     * @private
     */
    private readonly selectedGateway: string;

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private logService: LogService,
        private namespaceService: NamespaceService,
        private router: Router,
        private shuttleApiService: ShuttleApiService,
        private utilsService: UtilsService
    ) {
        super();
        this.selectedGateway = this.router.getCurrentNavigation()?.extras?.state?.selectedGateway;
        this.setupFormGroup();
    }

    ngOnInit(): void {
        this.selectedNamespaceKey = this.namespaceService.selectedNamespace.key;
        const shuttleId: string = this.activatedRoute.snapshot.paramMap.get('shuttleId');
        this.storedSubnetId = this.activatedRoute.snapshot.paramMap.get('subnetId') || null;
        if (shuttleId) {
            this.setUpPageData(parseInt(shuttleId, 10));
        }
    }

    /**
     * Toggle gateway type.
     * @param state
     */
    public toggleGatewayType(state: boolean) {
        this.newGateway = state;
    }

    /**
     * Cancel helper that sets the selected gateway on the parent component via navigation extras.
     */
    public cancel(): void {
        const formValues = this.formGroup.value;
        this.routeBack(formValues);
    }

    /**
     * Submit shuttle.
     */
    public submit(): Observable<any> {
        return new Observable<any>(observer => {
            const shuttleCopy = this.utilsService.duplicateObject(this.storedShuttle);
            const currentSubnets = shuttleCopy['subnets']?.length ? shuttleCopy['subnets'] : [];
            const formValues = this.formGroup.value;
            if (this.storedSubnetId) {
                currentSubnets[this.storedSubnetId] = formValues;
            } else {
                currentSubnets.push(formValues);
            }

            shuttleCopy['subnets'] = currentSubnets;
            this.shuttleApiService.update(this.selectedNamespaceKey, shuttleCopy.id, shuttleCopy)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.routeBack(formValues);
                        observer.next();
                        observer.complete();
                    },
                    error: (error: any): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Route back to shuttle edit page with gateway set in extras.
     * @param formValues
     * @private
     */
    private routeBack(formValues: { gateway: any; }): void {
        this.router.navigateByUrl(
            `/cm/adcs/shuttle/edit/${this.storedShuttle.id}`,
            { state: { newSubnetGateway: formValues.gateway } })
            .then().catch();
    }

    /**
     * Get the shuttle data.
     * @param id
     * @private
     */
    private setUpPageData(id: number): void {
        this.shuttleApiService.get(0, id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (shuttle: any): void => {
                    shuttle = this.formatShuttleSubnets(shuttle);
                    this.storedShuttle = shuttle;
                    this.patchDataToForm(shuttle);
                    this.setUpDropdown(shuttle.subnets);
                    this.setUpFormSubscriber();
                },
                error: (error: any): void => {
                    this.logService.error(
                        'AddEditSubnetComponent: setUpPageData',
                        'Unable to get Shuttle data for ID ' + id, error
                    );
                }
            });
    }

    /**
     * Add Ids to subnets for reference when editing and deleting.
     * @param shuttle
     * @private
     */
    private formatShuttleSubnets(shuttle: ShuttleModel): ShuttleModel {
        // Order subnets by gateway, GO doesn't have an order when there isn't an ID.
        const sortedSubnets = this.utilsService.sortArrayByProperty(shuttle.subnets, 'gateway');
        shuttle.subnets = sortedSubnets.map((item: any, index: number) => {
            item['id'] = index;

            return item;
        });

        return shuttle;
    }

    /**
     * Setup user form.
     * @private
     */
    private setupFormGroup(): void {
        this.formGroup = this.formBuilder.group({
            gateway: [null, [Validators.required, ipValidator]],
            subnet: [null, [Validators.required, ValidationService.formIPCIDRSubnetValidator]],
            subnetLabel: [null, Validators.required],
        });
    }

    /**
     * Set up form subscriber.
     * @private
     */
    private setUpFormSubscriber(): void {
        const subnetFormField = this.formGroup.get('subnet');
        const subnets = this.storedShuttle?.subnets.map(item => item.subnet);
        const currentSubnet = this.storedShuttle?.subnets.find(item => item.id.toString() === this.storedSubnetId);

        subnetFormField.valueChanges
            .subscribe({
                next: (value: string): void => {
                    if (subnets.includes(value) && currentSubnet?.subnet !== value) {
                        subnetFormField.setErrors({ 'duplicateSubnet': `${this.languageService.translate('duplicate_subnet')}` });
                        subnetFormField.markAsTouched();
                    }
                }
            });
    }

    /**
     * Patch shuttle data to form.
     * @param shuttle
     * @private
     */
    private patchDataToForm(shuttle: ShuttleModel): void {
        const subnet = shuttle.subnets[this.storedSubnetId];
        this.formGroup.patchValue(subnet);
    }

    /**
     * Setup gateway dropdown.
     * @param subnets
     * @private
     */
    private setUpDropdown(subnets: any []): void {
        const gateways = [];
        const excludedGateways = ['127.0.0.1', 'localhost', '::1'];
        for (const item of subnets) {
            const keys = gateways.map(item => item.key);
            if (!keys.includes(item.gateway) && !excludedGateways.includes(item.gateway)) {
                gateways.push({ key: item.gateway, value: item.gateway });
            }
        }

        this.dropdown.gateways = gateways;
        this.formGroup.get('gateway').setValue(this.selectedGateway
            ? this.selectedGateway
            : this.dropdown.gateways[0].key);
    }
}
